import React, { FormEvent, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { useLoginMutation } from "../../store/api/authApi";
import "./AdminLoginModal.css";

interface AdminLoginModalProps {
  show: boolean;
  onHide?: () => void;
  forceOpen?: boolean;
}

const AdminLoginModal: React.FC<AdminLoginModalProps> = ({
  show,
  onHide,
  forceOpen = false,
}) => {
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (show) return;
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");
  }, [show]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      const result = await login({ email, password }).unwrap();
      if (!result?.token) {
        setError("Invalid email or password.");
        return;
      }
      onHide?.();
      navigate("/admin");
    } catch (apiError) {
      setError(getApiErrorMessage(apiError as FetchBaseQueryError));
    }
  };

  return (
    <Modal
      show={show}
      onHide={forceOpen ? undefined : onHide}
      centered
      backdrop={false}
      keyboard={!forceOpen}
      dialogClassName='admin-login-dialog'
      className='admin-login-modal'
    >
      <Modal.Body className='admin-login-body'>
        {/* Brand header */}
        <div className='al-brand'>
          <div className='al-brand-icon'>
            <i className='bi bi-shield-lock-fill' />
          </div>
          <div>
            <div className='al-brand-name'>Dialin24</div>
            <div className='al-brand-sub'>Admin Portal</div>
          </div>
        </div>

        <h2 className='admin-login-title'>Sign in to your account</h2>
        <p className='admin-login-copy'>
          Enter your credentials to access the admin control panel.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          {/* Email */}
          <div className='al-field'>
            <label htmlFor='adminEmail' className='admin-login-label'>
              Email address
            </label>
            <div className='al-input-wrap'>
              <i className='bi bi-envelope al-input-icon' />
              <input
                id='adminEmail'
                type='email'
                className='admin-login-input'
                placeholder='admin@example.com'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete='email'
                required
              />
            </div>
          </div>

          {/* Password */}
          <div className='al-field'>
            <div className='al-label-row'>
              <label htmlFor='adminPassword' className='admin-login-label'>
                Password
              </label>
              <Link to='/forgot-password' className='al-forgot-link'>
                Forgot password?
              </Link>
            </div>
            <div className='al-input-wrap'>
              <i className='bi bi-lock al-input-icon' />
              <input
                id='adminPassword'
                type={showPassword ? "text" : "password"}
                className='admin-login-input al-input-padded-r'
                placeholder='••••••••'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete='current-password'
                required
              />
              <button
                type='button'
                className='al-eye-btn'
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i
                  className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
                />
              </button>
            </div>
          </div>

          {error && <div className='admin-login-error'>{error}</div>}

          <button
            type='submit'
            className='btn admin-login-submit w-100'
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                />
                Signing in…
              </>
            ) : (
              <>
                <i className='bi bi-box-arrow-in-right me-2' />
                Sign In
              </>
            )}
          </button>

          {!forceOpen && (
            <button
              type='button'
              className='btn admin-login-cancel w-100 mt-2'
              onClick={onHide}
              disabled={isLoading}
            >
              Cancel
            </button>
          )}
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AdminLoginModal;
